import { Dialog } from "components/Dialog";
import { useEffect, useRef, useState } from "react";
import {
  BOLDSIGN_ORIGIN,
  BoldSignData,
  BoldSignSigningClientSideAction,
} from "./boldSignClientSideEvents";
import { Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface EmbeddedDocumentSigningProps {
  open: boolean;
  onClose: () => void;
  signingUrl: string;
  onSigned: () => void;
}

export function EmbeddedDocumentSigning({
  open,
  onClose,
  signingUrl,
  onSigned,
}: EmbeddedDocumentSigningProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const onMessage = (e: MessageEvent<BoldSignData>) => {
      if (e.origin !== BOLDSIGN_ORIGIN) return;

      const data = e.data && (e.data as BoldSignData);
      const action = (
        typeof data === "string" ? data : data?.action
      ) as BoldSignSigningClientSideAction;
      if (!action) return;

      if (action === "onDocumentSigned") {
        setIsSuccess(true);
        onSigned();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onSigned, onClose]);

  if (isSuccess) {
    return (
      <Dialog
        title="Sign Document"
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        noPrimaryAction
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          height="100%"
          pt={4}
          pb={8}
        >
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
          <Typography>Document successfully signed</Typography>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Sign Document"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      contentSx={{ height: "100%", p: 0 }}
      DialogContentProps={{ sx: { p: 0 } }}
      noPrimaryAction
    >
      {signingUrl && (
        <iframe
          ref={iframeRef}
          src={signingUrl}
          width="100%"
          height="100%"
          frameBorder="0"
        />
      )}
    </Dialog>
  );
}
